import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDirection } from '../../../contexts/DirectionContext';
import { useBranding } from '../../../contexts/BrandingContext';
import { BrandLogo, ThemeToggle, LanguageSwitcher, SocialMediaLinks } from '../../common';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { branding } = useBranding();

  const socialLinks = [
    { icon: 'ph:facebook-logo-thin', href: branding.socialLinks?.facebook || '#' },
    { icon: 'ph:twitter-logo-thin', href: branding.socialLinks?.twitter || '#' },
    { icon: 'ph:instagram-logo-thin', href: branding.socialLinks?.instagram || '#' },
    { icon: 'ph:youtube-logo-thin', href: branding.socialLinks?.youtube || '#' },
  ];

  const footerSections = [
    {
      title: t('footer.about'),
      links: [
        { label: t('navigation.about'), href: '/about' },
        { label: t('navigation.contact'), href: '/contact' },
      ],
    },
    {
      title: t('footer.explore'),
      links: [
        { label: t('navigation.content'), href: '/content' },
        { label: t('navigation.topics'), href: '/topics' },
        { label: t('navigation.magazine'), href: '/magazine' },
        { label: t('navigation.askRabbi'), href: '/ask' },
      ],
    },
    {
      title: t('footer.actions'),
      links: [
        { label: t('navigation.donate'), href: '/donate' },
        { label: t('navigation.live'), href: '/live' },
        { label: t('navigation.login'), href: '/login' },
        { label: t('navigation.register'), href: '/register' },
      ],
    },
  ];

  return (
    <footer className="
      bg-background-primary
      border-t border-border-primary
      transition-colors duration-200
    ">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
          {/* Brand Section */}
          <div className={`
            ${isRTL ? 'text-right' : 'text-left'}
            lg:col-span-4 lg:ltr:mr-16 lg:rtl:ml-16
          `}>
            <BrandLogo showLink={true} />
            <p className="
              mt-4 text-sm
              text-text-secondary
              transition-colors duration-200
            ">
              {t('footer.description')}
            </p>
            
            {/* Dedication */}
            <div className="mt-3">
              <p className="text-sm text-text-secondary italic font-light">
                {t('dedication.translation')}
              </p>
            </div>
            
            <div className="mt-6">
              <SocialMediaLinks 
                links={socialLinks}
                iconClassName="w-5 h-5 text-text-secondary hover:text-primary-500 transition-colors duration-200"
                className="justify-start"
              />
            </div>
          </div>

          {/* Footer Sections */}
          {footerSections.map((section) => (
            <div
              key={section.title}
              className={`
                ${isRTL ? 'text-right' : 'text-left'}
                lg:col-span-2
              `}
            >
              <h3 className="
                text-sm font-bold
                text-text-primary
                uppercase tracking-wider
                transition-colors duration-200
              ">
                {section.title}
              </h3>
              <ul className="mt-4 space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="
                        text-sm
                        text-text-secondary
                        hover:text-primary-500
                        transition-colors duration-200
                      "
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Contact & Language Section */}
          <div className={`
            ${isRTL ? 'text-right' : 'text-left'}
            lg:col-span-2 lg:col-start-11
          `}>
            <h3 className="
              text-sm font-bold
              text-text-primary
              uppercase tracking-wider
              transition-colors duration-200
            ">
              {t('navigation.preferences')}
            </h3>
            <div className="mt-4 flex items-center gap-4">
              <ThemeToggle />
              <LanguageSwitcher openUpward={true} />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="border-t border-border-primary">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-text-secondary transition-colors duration-200">
              &copy; {new Date().getFullYear()} {branding.organizationName}. {t('footer.rights')}
            </p>
            <div className="mt-4 md:mt-0">
              <div className="flex space-x-6 rtl:space-x-reverse">
                <Link
                  to="/privacy-policy"
                  className="
                    text-xs
                    text-text-secondary
                    hover:text-primary-500
                    transition-colors duration-200
                  "
                >
                  {t('common.privacyPolicy')}
                </Link>
                <Link
                  to="/terms-of-service"
                  className="
                    text-xs
                    text-text-secondary
                    hover:text-primary-500
                    transition-colors duration-200
                  "
                >
                  {t('common.termsOfService')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 